import {Injectable} from '@angular/core';
import {Currency} from '@app/graphql-typings/graphql';

@Injectable({
  providedIn: 'root'
})
export class CountryAndTimezoneService {

  constructor() {}

  // Note: countries should be ordered alphabetically
  readonly COUNTRY_AUSTRALIA = 'Australia';
  readonly COUNTRY_AUSTRIA = 'Austria';
  readonly COUNTRY_BRAZIL = 'Brazil';
  readonly COUNTRY_CANADA = 'Canada';
  readonly COUNTRY_CHINA = 'China';
  readonly COUNTRY_FINLAND = 'Finland';
  readonly COUNTRY_INDONESIA = 'Indonesia';
  readonly COUNTRY_IRELAND = 'Ireland';
  readonly COUNTRY_LITHUANIA = 'Lithuania';
  readonly COUNTRY_NEW_ZEALAND = 'New Zealand';
  readonly COUNTRY_SINGAPORE = 'Singapore';
  readonly COUNTRY_SWEDEN = 'Sweden';
  readonly COUNTRY_SWITZERLAND = 'Switzerland';
  readonly COUNTRY_UK = 'United Kingdom';
  readonly COUNTRY_USA = 'USA';

  // Note: timezones should be ordered alphabetically
  readonly AMERICA_CHICAGO = 'America/Chicago';
  readonly ASIA_JAKARTA = 'Asia/Jakarta';
  readonly ASIA_SINGAPORE = 'Asia/Singapore';
  readonly ASIA_SHANGHAI = 'Asia/Shanghai';
  readonly AUSTRALIA_BRISBANE = 'Australia/Brisbane';
  readonly AUSTRIA_VIENNA = 'Europe/Vienna';
  readonly BRAZIL_ACRE = 'Brazil/Acre';
  readonly BRAZIL_DENORONHA = 'Brazil/DeNoronha';
  readonly BRAZIL_EAST = 'Brazil/East';
  readonly BRAZIL_WEST = 'Brazil/West';
  readonly CANADA_MOUNTAIN = 'Canada/Mountain';
  readonly EUROPE_DUBLIN = 'Europe/Dublin';
  readonly EUROPE_HELSINKI = 'Europe/Helsinki';
  readonly EUROPE_LONDON = 'Europe/London';
  readonly EUROPE_STOCKHOLM = 'Europe/Stockholm';
  readonly EUROPE_VILNIUS = 'Europe/Vilnius';
  readonly EUROPE_ZURICH = 'Europe/Zurich';
  readonly PACIFIC_AUCKLAND = 'Pacific/Auckland';

  // Note: countries should be ordered alphabetically
  readonly COUNTRIES: Readonly<string[]> = [this.COUNTRY_AUSTRALIA, this.COUNTRY_AUSTRIA, this.COUNTRY_BRAZIL, this.COUNTRY_CANADA,
    this.COUNTRY_CHINA, this.COUNTRY_FINLAND, this.COUNTRY_INDONESIA, this.COUNTRY_IRELAND, this.COUNTRY_LITHUANIA, this.COUNTRY_NEW_ZEALAND, this.COUNTRY_SINGAPORE,
    this.COUNTRY_SWEDEN, this.COUNTRY_SWITZERLAND, this.COUNTRY_UK, this.COUNTRY_USA];

  // Note: timezones by country should be ordered by name of country alphabetically
  readonly TIMEZONES_BY_COUNTRY: Readonly<Map<string, string[]>> = new Map([
    [this.COUNTRY_AUSTRALIA, ['Australia/ACT', 'Australia/Adelaide', this.AUSTRALIA_BRISBANE, 'Australia/Broken_Hill',
      'Australia/Canberra', 'Australia/Currie', 'Australia/Darwin', 'Australia/Eucla', 'Australia/Hobart',
      'Australia/LHI', 'Australia/Lindeman', 'Australia/Lord_Howe', 'Australia/Melbourne', 'Australia/North',
      'Australia/NSW', 'Australia/Perth', 'Australia/Queensland', 'Australia/South', 'Australia/Sydney',
      'Australia/Tasmania', 'Australia/Victoria', 'Australia/West', 'Australia/Yancowinna']],
    [this.COUNTRY_AUSTRIA, [this.AUSTRIA_VIENNA]],
    [this.COUNTRY_BRAZIL, [this.BRAZIL_ACRE, this.BRAZIL_DENORONHA, this.BRAZIL_EAST, this.BRAZIL_WEST]],
    [this.COUNTRY_CANADA, ['Canada/Atlantic', 'Canada/Central', 'Canada/Eastern', this.CANADA_MOUNTAIN, 'Canada/Newfoundland',
      'Canada/Pacific', 'Canada/Saskatchewan', 'Canada/Yukon']],
    [this.COUNTRY_CHINA, [this.ASIA_SHANGHAI]],
    [this.COUNTRY_FINLAND, [this.EUROPE_HELSINKI]],
    [this.COUNTRY_IRELAND, [this.EUROPE_DUBLIN]],
    [this.COUNTRY_INDONESIA, [this.ASIA_JAKARTA, 'Asia/Jayapura', 'Asia/Makassar', 'Asia/Pontianak', 'Asia/Ujung_Pandang']],
    [this.COUNTRY_LITHUANIA, [this.EUROPE_VILNIUS]],
    [this.COUNTRY_NEW_ZEALAND, [this.PACIFIC_AUCKLAND]],
    [this.COUNTRY_SINGAPORE, [this.ASIA_SINGAPORE]],
    [this.COUNTRY_SWEDEN, [this.EUROPE_STOCKHOLM]],
    [this.COUNTRY_SWITZERLAND, [this.EUROPE_ZURICH]],
    [this.COUNTRY_UK, [this.EUROPE_LONDON, 'Europe/Guernsey']],
    [this.COUNTRY_USA, ['America/Adak', 'America/Anchorage', 'America/Boise', this.AMERICA_CHICAGO, 'America/Denver', 'America/Detroit',
      'America/Indiana/Indianapolis', 'America/Indiana/Knox', 'America/Indiana/Marengo', 'America/Indiana/Petersburg',
      'America/Indiana/Tell_City', 'America/Indiana/Vevay', 'America/Indiana/Vincennes', 'America/Indiana/Winamac', 'America/Juneau',
      'America/Kentucky/Louisville', 'America/Kentucky/Monticello', 'America/Los_Angeles', 'America/Menominee', 'America/Metlakatla',
      'America/New_York', 'America/Nome', 'America/North_Dakota/Beulah', 'America/North_Dakota/Center', 'America/North_Dakota/New_Salem',
      'America/Phoenix', 'America/Sitka', 'America/Yakutat', 'Pacific/Honolulu']]
  ]);

  // Note: states by country should be ordered by name of country alphabetically, and the states order by name alphabetically as well
  readonly statesByCountry: Map<string, string[]> = new Map([
    [this.COUNTRY_AUSTRALIA, ['Australian Capital Territory', 'Jervis Bay Territory', 'New South Wales', 'Northern Territory',
      'Queensland', 'South Australia', 'Tasmania', 'Victoria', 'Western Australia']],
    [this.COUNTRY_AUSTRIA, ['Burgenland', 'K\u00E4rnten', 'Nieder-\u00F6sterreich', 'Salzburg', 'Steiermark', 'Ober-\u00F6sterreich',
      'Wien', 'Vorarlberg']],
    [this.COUNTRY_BRAZIL, ['Acre', 'Alagoas', 'Amap\u00E1', 'Amazonas', 'Bahia', 'Cear\u00E1', 'Distrito Federal',
      'Esp\u00EDrito Santo', 'Goi\u00E1s', 'Maranh\u00E3o', 'Mato Grosso', 'Mato Grosso do Sul', 'Minas Gerais',
      'Par\u00E1', 'Para\u00EDba', 'Paran\u00E1', 'Pernambuco', 'Piau\u00ED', 'Rio de Janeiro', 'Rio Grande do Norte',
      'Rio Grande do Sul', 'Rond\u00F4nia', 'Roraima', 'Santa Catarina', 'S\u00E3o Paulo', 'Sergipe', 'Tocantins']],
    [this.COUNTRY_CANADA, ['Alberta', 'British Columbia', 'Manitoba', 'Newfoundland and Labrador', 'New Brunswick',
      'Northwest Territories', 'Nova Scotia', 'Nunavut', 'Ontario', 'Prince Edward Island', 'Quebec', 'Saskatchewan', 'Yukon']],
    [this.COUNTRY_CHINA, ['Anhui Province', 'Beijing Municipality', 'Chongqing Municipality', 'Fujian Province', 'Guangdong Province', 'Gansu Province',
      'Guangxi Zhuang Autonomous Region', 'Guizhou Province', 'Henan Province', 'Hubei Province', 'Hebei Province', 'Hainan Province',
      'Hong Kong Special Administrative Region', 'Heilongjiang Province', 'Hunan Province', 'Jilin Province', 'Jiangsu Province', 'Jiangxi Province',
      'Liaoning Province', 'Macau Special Administrative Region', 'Inner Mongolia Autonomous Region', 'Ningxia Hui Autonomous Region', 'Qinghai Province',
      'Sichuan Province', 'Shandong Province', 'Shanghai Municipality', 'Shaanxi Province', 'Shanxi Province', 'Tianjin Municipality', 'Taiwan Province',
      'Xinjiang Uyghur Autonomous Region', 'Tibet Autonomous Region', 'Yunnan Province', 'Zhejiang Province']],
    [this.COUNTRY_FINLAND, ['\u00c5land', 'Central Finland', 'Central Ostrobothnia', 'Kainuu', 'Kanta-H\u00e4me', 'Kymenlaakso', 'Lapland', 'North Karelia',
      'North Ostrobothnia', 'North Savo', 'Ostrobothnia', 'P\u00e4ij\u00e4t-H\u00e4me', 'Pirkanmaa', 'Satakunta', 'South Karelia', 'South Ostrobothnia', 'South Savo',
      'Southwest Finland', 'Uusimaa']],
    [this.COUNTRY_NEW_ZEALAND, ['Auckland', 'Bay of Plenty', 'Canterbury', 'Gisborne', 'Hawke\'s Bay', 'Manawat\u016b-Whanganui', 'Marlborough', 'Nelson',
      'Northland', 'Otago', 'Southland', 'Taranaki', 'Tasman', 'Waikato', 'Wellington', 'West Coast']],
    [this.COUNTRY_INDONESIA, ['Aceh', 'Bali', 'Bangka Belitung Islands', 'Banten', 'Bengkulu', 'Central Java', 'Central Kalimantan', 'Central Sulawesi',
      'East Java', 'East Kalimantan', 'East Nusa Tenggara', 'Gorontalo', 'Capital Special Region of Jakarta', 'Jambi', 'Lampung', 'Maluku',
      'North Kalimantan', 'North Maluku', 'North Sulawesi', 'North Sumatra', 'Papua', 'Riau', 'Riau Islands', 'Southeast Sulawesi', 'South Kalimantan',
      'South Sulawesi', 'South Sumatra', 'West Java', 'West Kalimantan', 'West Nusa Tenggara', 'West Papua', 'West Sulawesi', 'West Sumatra',
      'Special Region of Yogyakarta']],
    [this.COUNTRY_IRELAND, ['Carlow', 'Cavan', 'Clare', 'Cork', 'Donegal', 'Dublin', 'Galway', 'Kerry', 'Kildare', 'Kilkenny',
      'Laois', 'Leitrim', 'Limerick', 'Longford', 'Louth', 'Mayo', 'Meath', 'Monaghan', 'Offaly', 'Roscommon', 'Sligo', 'Tipperary',
      'Waterford', 'Westmeath', 'Wexford', 'Wicklow']],
    [this.COUNTRY_LITHUANIA, ['Alytaus', 'Kauno', 'Klaip\u0117dos', 'Marijampol\u0117s', 'Panev\u0117\u017eio', '\u0160iauli\u0173', 'Taurag\u0117s',
      'Tel\u0161i\u0173', 'Utenos', 'Vilniaus']],
    [this.COUNTRY_SINGAPORE, ['Singapore']],
    [this.COUNTRY_SWEDEN, ['\u00c5ngermanland', 'Blekinge', 'Bohusl\u00e4n', 'Dalarna', 'Dalsland', 'G\u00e4strikland', 'Gotland',
      'Halland', 'H\u00e4lsingland', 'H\u00e4rjedalen', 'J\u00e4mtland', 'Lappland', 'Medelpad', 'N\u00e4rke', 'Norrbotten', '\u00d6land',
      '\u00d6sterg\u00f6tland', 'Sk\u00c5ne', 'Sm\u00c5land', 'S\u00f6dermanland', 'Uppland', 'V\u00e4rmland', 'V\u00e4sterbotten',
      'V\u00e4sterg\u00f6tland', 'V\u00e4stmanland']],
    [this.COUNTRY_SWITZERLAND, ['Aargau', 'Appenzell Ausserrhoden', 'Appenzell Innerrhoden', 'Basel-Landschaft', 'Basel-Stadt', 'Bern',
      'Fribourg', 'Gen\u00e8ve', 'Glarus', 'Graub\u00fcnden', 'Jura', 'Luzern', 'Neuch\u00e2tel', 'Nidwalden', 'Obwalden', 'Schaffhausen',
      'Schwyz', 'Solothurn', 'St. Gallen', 'Thurgau', 'Ticino', 'Uri', 'Valais', 'Vaud', 'Zug', 'Z\u00fcrich']],
    [this.COUNTRY_UK, ['England', 'Northern Ireland', 'Scotland', 'Wales']],
    [this.COUNTRY_USA, ['Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware', 'District of Columbia',
      'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky', 'Louisiana', 'Maine', 'Maryland', 'Massachusetts',
      'Michigan', 'Minnesota', 'Mississippi', 'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico', 'New York',
      'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania', 'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee',
      'Texas', 'Utah', 'Vermont', 'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming']]]);

  getCountryFromTimezone(timezone: string): string {
    const countryList: string[] = Array.from(this.TIMEZONES_BY_COUNTRY.keys());
    return countryList.find(c => this.TIMEZONES_BY_COUNTRY.get(c).find(t => t === timezone));
  }

  // Using ISO 4217 codes same as currency pipe https://en.wikipedia.org/wiki/ISO_4217
  getCurrencyCode(currency: Currency): string {
    switch (currency) {
      case Currency.AUSTRALIAN_DOLLAR:
        return 'AUD';
      case Currency.BRAZILIAN_REAL:
        return 'BRL';
      case Currency.CANADIAN_DOLLAR:
        return 'CAD';
      case Currency.CHINESE_YUAN:
        return 'CNY';
      case Currency.EURO:
        return 'EUR';
      case Currency.INDONESIAN_RUPIAH:
        return 'IDR';
      case Currency.NEW_ZEALAND_DOLLAR:
        return 'NZD';
      case Currency.POUND_STERLING:
        return 'GBP';
      case Currency.SINGAPORE_DOLLAR:
        return 'SGD';
      case Currency.SWEDISH_KRONA:
        return 'SEK';
      case Currency.SWISS_FRANC:
        return 'CHF';
      case Currency.UNITED_STATES_DOLLAR:
        return 'USD';
      default:
        throw new Error('Cannot find currency code for currency ' + currency);
      }
  }
}
